import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useToastStore } from '../../store/toastStore';

// --- Types ---

type ContractType = 'rent' | 'sale' | 'reservation';
type ContractStatus =
  | 'draft'
  | 'documents_requested'
  | 'documents_received'
  | 'pending_signature'
  | 'signed'
  | 'active'
  | 'expired'
  | 'cancelled';

interface Contract {
  id: number;
  property_id: number;
  property_title: string;
  tenant_user_id: number;
  tenant_name: string | null;
  contract_type: ContractType;
  status: ContractStatus;
  monthly_amount: number;
  currency: string;
  start_date: string;
  end_date: string;
  created_at: string;
  renewed_from_id?: number | null;
}

interface ContractsResponse {
  data: Contract[];
  meta?: {
    total: number;
    page: number;
    page_size: number;
  };
}

interface RenewResponse {
  data: Contract;
}

// --- Constants ---

const CONTRACT_TYPE_LABEL: Record<ContractType, string> = {
  rent: 'Alquiler',
  sale: 'Venta',
  reservation: 'Reserva',
};

const CONTRACT_TYPE_BADGE: Record<ContractType, string> = {
  rent: 'badge-info',
  sale: 'badge-secondary',
  reservation: 'badge-accent',
};

const STATUS_LABEL: Record<ContractStatus, string> = {
  draft: 'Borrador',
  documents_requested: 'Documentos solicitados',
  documents_received: 'Documentos recibidos',
  pending_signature: 'Pendiente firma',
  signed: 'Firmado',
  active: 'Activo',
  expired: 'Vencido',
  cancelled: 'Cancelado',
};

const STATUS_BADGE: Record<ContractStatus, string> = {
  draft: 'badge-ghost',
  documents_requested: 'badge-info',
  documents_received: 'badge-accent',
  pending_signature: 'badge-warning',
  signed: 'badge-primary',
  active: 'badge-success',
  expired: 'badge-error',
  cancelled: 'badge-neutral',
};

// --- Helpers ---

function daysRemaining(endDate: string): number {
  const end = new Date(endDate);
  const now = new Date();
  const diff = end.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatCurrency(amount: number, currency: string): string {
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: currency || 'ARS',
    minimumFractionDigits: 0,
  }).format(amount);
}

// --- Contract History Chain ---

function ContractHistoryChain({ contract, allContracts }: { contract: Contract; allContracts: Contract[] }): React.ReactElement | null {
  const [open, setOpen] = useState(false);

  // Build chain going backwards via renewed_from_id
  const chain: Contract[] = [];
  const visited = new Set<number>();

  // Find the root (oldest) contract in the chain
  let current: Contract | undefined = contract;
  while (current?.renewed_from_id && !visited.has(current.id)) {
    visited.add(current.id);
    current = allContracts.find((c) => c.id === current!.renewed_from_id);
  }

  // Now walk forward: from root, find all renewals
  if (current) {
    chain.push(current);
    visited.clear();
    let next: Contract | undefined = current;
    while (next && !visited.has(next.id)) {
      visited.add(next.id);
      const renewal = allContracts.find((c) => c.renewed_from_id === next!.id);
      if (renewal) {
        chain.push(renewal);
        next = renewal;
      } else {
        break;
      }
    }
  }

  if (chain.length <= 1) return null;

  return (
    <div className="mt-1">
      <button
        className="btn btn-ghost btn-xs text-primary gap-1"
        onClick={() => setOpen(!open)}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-3.5 h-3.5">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Historial ({chain.length} contratos)
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>

      {open && (
        <div className="ml-4 mt-2 border-l-2 border-base-300 pl-4 space-y-2">
          {chain.map((c, idx) => (
            <div key={c.id} className="flex items-center gap-2 text-sm">
              <span className={`badge badge-sm ${idx === chain.length - 1 ? 'badge-primary' : 'badge-ghost'}`}>
                {idx === 0 ? 'Original' : `Renovacion ${idx}`}
              </span>
              <span className="text-base-content/70">
                {formatDate(c.start_date)} - {formatDate(c.end_date)}
              </span>
              <span className={`badge badge-sm ${STATUS_BADGE[c.status]}`}>
                {STATUS_LABEL[c.status]}
              </span>
              <Link to={`/app/contracts/${c.id}/edit`} className="link link-primary text-xs">
                Ver
              </Link>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Main Component ---

export default function ContractDashboardPage(): React.ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const addToast = useToastStore((s) => s.addToast);

  // Fetch active contracts
  const { data: activeRes, isLoading: loadingActive } = useQuery({
    queryKey: ['contracts', 'active'],
    queryFn: () => api.get<ContractsResponse>('/v1/contracts?status=active'),
  });

  // Fetch pending signature contracts
  const { data: pendingRes, isLoading: loadingPending } = useQuery({
    queryKey: ['contracts', 'pending_signature'],
    queryFn: () => api.get<ContractsResponse>('/v1/contracts?status=pending_signature'),
  });

  // Fetch all contracts (for history chain)
  const { data: allRes } = useQuery({
    queryKey: ['contracts', 'all-dashboard'],
    queryFn: () => api.get<ContractsResponse>('/v1/contracts'),
  });

  const renewMutation = useMutation({
    mutationFn: (contractId: number) =>
      api.post<RenewResponse>(`/v1/contracts/${contractId}/renew`),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['contracts'] });
      addToast('Contrato renovado exitosamente', 'success');
      const newContract = response.data;
      navigate(`/app/contracts/${newContract.id}/edit`);
    },
    onError: () => {
      addToast('Error al renovar el contrato', 'error');
    },
  });

  const activeContracts = activeRes?.data ?? [];
  const pendingContracts = pendingRes?.data ?? [];
  const allContracts = allRes?.data ?? [];

  // Expiring in 30 days
  const expiringContracts = activeContracts.filter((c) => {
    const days = daysRemaining(c.end_date);
    return days >= 0 && days <= 30;
  });

  // Total monthly revenue
  const totalRevenue = activeContracts.reduce((sum, c) => sum + c.monthly_amount, 0);
  const mainCurrency = activeContracts[0]?.currency || 'ARS';

  const isLoading = loadingActive || loadingPending;

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold">Panel de contratos</h1>
        <div className="flex gap-2">
          <Link to="/app/contracts" className="btn btn-ghost btn-sm">
            Ver todos los contratos
          </Link>
          <Link to="/app/contracts/new" className="btn btn-primary btn-sm">
            + Nuevo contrato
          </Link>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Active Contracts */}
        <div className="card bg-base-100 shadow border-l-4 border-success">
          <div className="card-body p-4">
            <h2 className="card-title text-sm text-base-content/60">Contratos activos</h2>
            <p className="text-3xl font-bold text-success">{activeContracts.length}</p>
          </div>
        </div>

        {/* Pending Signature */}
        <div className="card bg-base-100 shadow border-l-4 border-warning">
          <div className="card-body p-4">
            <h2 className="card-title text-sm text-base-content/60">Pendientes de firma</h2>
            <p className="text-3xl font-bold text-warning">{pendingContracts.length}</p>
          </div>
        </div>

        {/* Expiring in 30 days */}
        <div className="card bg-base-100 shadow border-l-4 border-error">
          <div className="card-body p-4">
            <h2 className="card-title text-sm text-base-content/60">Vencen en 30 dias</h2>
            <p className="text-3xl font-bold text-error">{expiringContracts.length}</p>
          </div>
        </div>

        {/* Monthly Revenue */}
        <div className="card bg-base-100 shadow border-l-4 border-primary">
          <div className="card-body p-4">
            <h2 className="card-title text-sm text-base-content/60">Ingreso mensual</h2>
            <p className="text-2xl font-bold text-primary">
              {formatCurrency(totalRevenue, mainCurrency)}
            </p>
          </div>
        </div>
      </div>

      {/* Expiring Soon Alert */}
      {expiringContracts.length > 0 && (
        <div className="card bg-base-100 shadow border border-error/30">
          <div className="card-body p-4">
            <h2 className="card-title text-base text-error gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              Contratos por vencer
            </h2>
            <div className="divide-y divide-base-300">
              {expiringContracts.map((contract) => {
                const days = daysRemaining(contract.end_date);
                return (
                  <div key={contract.id} className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 py-3">
                    <div className="flex-1">
                      <p className="font-medium">{contract.property_title}</p>
                      <p className="text-sm text-base-content/60">
                        {contract.tenant_name || 'Sin inquilino'} &middot; Vence: {formatDate(contract.end_date)}
                      </p>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`badge ${days <= 7 ? 'badge-error' : 'badge-warning'}`}>
                        {days} {days === 1 ? 'dia' : 'dias'}
                      </span>
                      <button
                        className="btn btn-sm btn-outline btn-primary"
                        onClick={() => renewMutation.mutate(contract.id)}
                        disabled={renewMutation.isPending}
                      >
                        {renewMutation.isPending ? (
                          <span className="loading loading-spinner loading-xs" />
                        ) : (
                          'Renovar'
                        )}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Active Contracts Table */}
      <div className="card bg-base-100 shadow">
        <div className="card-body p-4">
          <h2 className="card-title text-base mb-3">Contratos activos</h2>

          {activeContracts.length === 0 ? (
            <p className="text-base-content/60 text-center py-8">No hay contratos activos.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Propiedad</th>
                    <th>Inquilino</th>
                    <th>Tipo</th>
                    <th>Monto mensual</th>
                    <th>Inicio</th>
                    <th>Fin</th>
                    <th>Dias restantes</th>
                    <th>Estado</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {activeContracts.map((contract) => {
                    const days = daysRemaining(contract.end_date);
                    return (
                      <tr key={contract.id} className={days <= 30 ? 'bg-error/5' : ''}>
                        <td className="font-medium max-w-[200px] truncate">{contract.property_title}</td>
                        <td>{contract.tenant_name || '-'}</td>
                        <td>
                          <span className={`badge badge-sm ${CONTRACT_TYPE_BADGE[contract.contract_type]}`}>
                            {CONTRACT_TYPE_LABEL[contract.contract_type]}
                          </span>
                        </td>
                        <td>{formatCurrency(contract.monthly_amount, contract.currency)}</td>
                        <td>{formatDate(contract.start_date)}</td>
                        <td>{formatDate(contract.end_date)}</td>
                        <td>
                          <span className={`badge badge-sm ${days <= 7 ? 'badge-error' : days <= 30 ? 'badge-warning' : 'badge-ghost'}`}>
                            {days}
                          </span>
                        </td>
                        <td>
                          <span className={`badge badge-sm ${STATUS_BADGE[contract.status]}`}>
                            {STATUS_LABEL[contract.status]}
                          </span>
                        </td>
                        <td>
                          <div className="flex gap-1">
                            <Link
                              to={`/app/contracts/${contract.id}/edit`}
                              className="btn btn-ghost btn-xs"
                            >
                              Ver
                            </Link>
                            <button
                              className="btn btn-ghost btn-xs text-primary"
                              onClick={() => renewMutation.mutate(contract.id)}
                              disabled={renewMutation.isPending}
                            >
                              Renovar
                            </button>
                          </div>
                          <ContractHistoryChain contract={contract} allContracts={allContracts} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pending Signature Section */}
      {pendingContracts.length > 0 && (
        <div className="card bg-base-100 shadow">
          <div className="card-body p-4">
            <h2 className="card-title text-base mb-3">Pendientes de firma</h2>
            <div className="overflow-x-auto">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>Propiedad</th>
                    <th>Inquilino</th>
                    <th>Tipo</th>
                    <th>Monto mensual</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {pendingContracts.map((contract) => (
                    <tr key={contract.id}>
                      <td className="font-medium">{contract.property_title}</td>
                      <td>{contract.tenant_name || '-'}</td>
                      <td>
                        <span className={`badge badge-sm ${CONTRACT_TYPE_BADGE[contract.contract_type]}`}>
                          {CONTRACT_TYPE_LABEL[contract.contract_type]}
                        </span>
                      </td>
                      <td>{formatCurrency(contract.monthly_amount, contract.currency)}</td>
                      <td>
                        <Link
                          to={`/app/contracts/${contract.id}/edit`}
                          className="btn btn-ghost btn-xs"
                        >
                          Ver
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
